/**
 * Trips hub — container for Plans, Activities, and Routes.
 *
 * Renders a segmented button at the top for switching between views.
 * The active tab is driven by the URL: /activities, /plans, /routes.
 * Clicking a segment navigates via the router so browser history works
 * naturally for back buttons/gestures.
 */

import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { router } from '../../utils/router.js'
import './plan-list.js'
import './activity-list.js'
import './routes-list.js'

export type TripsTab = 'plans' | 'activities' | 'routes'

@customElement('cairn-logbook')
export class CairnLogbook extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      background: var(--cairn-background-color, #f8fafc);
      overflow: hidden;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex: 0 0 auto;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
    }

    .back-button:hover {
      background: rgba(59, 130, 246, 0.04);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    /* Hide header on desktop - title shown in main app header */
    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }

    /* Segmented button container */
    .tab-bar {
      display: flex;
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      flex: 0 0 auto;
    }

    .segmented-button {
      display: flex;
      width: 100%;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 20px;
      overflow: hidden;
      background: var(--cairn-surface-color, #ffffff);
    }

    .segment {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 16px;
      border: none;
      background: none;
      cursor: pointer;
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: 500;
      color: var(--cairn-text-secondary-color, #64748b);
      transition: all var(--cairn-animation-duration, 0.2s);
      white-space: nowrap;
    }

    .segment:not(:last-child) {
      border-right: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .segment:hover {
      background: rgba(59, 130, 246, 0.04);
    }

    .segment.active {
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
    }

    .segment .material-icons {
      font-size: 18px;
      font-family: 'Material Symbols Outlined';
    }

    /* Content area */
    .content {
      flex: 1;
      overflow: hidden;
      min-height: 0;
    }

    .content > * {
      height: 100%;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      user-select: none;
    }
  `

  @property({ type: String })
  tab: TripsTab = 'activities'

  private navigateTab(tab: TripsTab): void {
    if (tab === this.tab) return
    // Replace rather than push so back returns to the previous page, not the
    // previously-viewed tab.
    router.navigate(`/${tab}`, { replace: true })
  }

  private handleBack(): void {
    window.history.back()
  }

  private renderTabContent(): unknown {
    switch (this.tab) {
      case 'plans':
        return html`<plan-list></plan-list>`
      case 'activities':
        return html`<activity-list></activity-list>`
      case 'routes':
        return html`<routes-list></routes-list>`
    }
  }

  override render(): unknown {
    return html`
      <div class="header">
        <button class="back-button" @click=${this.handleBack} aria-label="Go back">
          <span class="material-icons">arrow_back</span>
        </button>
        <h1 class="title">Trips</h1>
      </div>

      <div class="tab-bar">
        <div class="segmented-button" role="tablist">
          <button
            class="segment ${this.tab === 'activities' ? 'active' : ''}"
            role="tab"
            aria-selected=${this.tab === 'activities'}
            @click=${() => this.navigateTab('activities')}
          >
            <span class="material-icons">directions_walk</span>
            Activities
          </button>
          <button
            class="segment ${this.tab === 'plans' ? 'active' : ''}"
            role="tab"
            aria-selected=${this.tab === 'plans'}
            @click=${() => this.navigateTab('plans')}
          >
            <span class="material-icons">map</span>
            Plans
          </button>
          <button
            class="segment ${this.tab === 'routes' ? 'active' : ''}"
            role="tab"
            aria-selected=${this.tab === 'routes'}
            @click=${() => this.navigateTab('routes')}
          >
            <span class="material-icons">alt_route</span>
            Routes
          </button>
        </div>
      </div>

      <div class="content">${this.renderTabContent()}</div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-logbook': CairnLogbook
  }
}
